import React, { useState, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { Chip } from '../Chip/Chip.es.js';
import { Input } from '../Input/Input.es.js';
import { Box } from '../../primitives/Box/Box.es.js';
import { forwardRef } from '../../system/forwardRef.es.js';

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const ChipWrapper = styled(Box)`
    display: grid;
    grid-gap: xs;
    grid-auto-flow: column;
    overflow-x: scroll;
    margin-left: 2px;
    -ms-overflow-style: none; /* IE 11 */
    scrollbar-width: none; /* Firefox 64 */
    &::-webkit-scrollbar {
        display: none;
    }
`;
const defaultChip = forwardRef((_a, ref) => {
  var _b = _a, { value } = _b, props = __objRest(_b, ["value"]);
  return /* @__PURE__ */ React.createElement(Chip, __spreadValues({
    ref
  }, props), value);
});
const ChipInput = forwardRef((_c, ref) => {
  var _d = _c, {
    shape,
    size = "md",
    variant,
    limitChipValues = 0,
    isDisabled,
    isReadOnly: isReadOnlyProp,
    id,
    type,
    autoComplete,
    placeholder,
    wrapperRef: wrapperRefProp = null,
    inputRef = null,
    splitSymbols = ", ",
    regExpForInputText = "^[a-zA-Z ]*$",
    getResult,
    values,
    onChange,
    onKeyDown,
    onFocus,
    onBlur,
    onAdd,
    onRemove,
    addChipOnBlur,
    value = "",
    getItemsLabel = (value2) => value2,
    chipComponent: ChipComponent = defaultChip,
    addonLeft,
    isMultiline
  } = _d, props = __objRest(_d, [
    "shape",
    "size",
    "variant",
    "limitChipValues",
    "isDisabled",
    "isReadOnly",
    "id",
    "type",
    "autoComplete",
    "placeholder",
    "wrapperRef",
    "inputRef",
    "splitSymbols",
    "regExpForInputText",
    "getResult",
    "values",
    "onChange",
    "onKeyDown",
    "onFocus",
    "onBlur",
    "onAdd",
    "onRemove",
    "addChipOnBlur",
    "value",
    "getItemsLabel",
    "chipComponent",
    "addonLeft",
    "isMultiline"
  ]);
  const [inputValue, setInputValue] = useState(value);
  const [chipArrayValues, setChipArrayValues] = useState([]);
  const [isReadOnly, setReadOnly] = useState(isReadOnlyProp);
  const createRegExpFromSplitSymbols = useCallback(({ isForStringMatch }) => {
    const symbolsArray = Array.from(splitSymbols);
    const pattern = `[${symbolsArray.join("|")}]`;
    return isForStringMatch ? pattern : new RegExp(pattern);
  }, [splitSymbols]);
  useEffect(() => {
    if (inputValue.match(createRegExpFromSplitSymbols({ isForStringMatch: true })) && inputValue.length > 1) {
      const replace = inputValue.replace(createRegExpFromSplitSymbols({ isForStringMatch: false }), "");
      const newValues = [...chipArrayValues, replace.trim()];
      setChipArrayValues(newValues);
      setInputValue("");
      if (onAdd) {
        onAdd(newValues);
      }
    }
  }, [chipArrayValues, createRegExpFromSplitSymbols, inputValue, onAdd]);
  useEffect(() => {
    if (getResult) {
      getResult(chipArrayValues);
    }
  }, [chipArrayValues, getResult]);
  useEffect(() => {
    if (values) {
      setChipArrayValues(values);
      setInputValue("");
    }
  }, [values]);
  useEffect(() => {
    if (limitChipValues === 0) {
      return;
    }
    setReadOnly((chipArrayValues == null ? void 0 : chipArrayValues.length) >= limitChipValues);
  }, [chipArrayValues, limitChipValues]);
  const handleInputChange = (event) => {
    const { value: value2 } = event.target;
    const regExp = new RegExp(regExpForInputText);
    const isFirstCharIsOneOfSplitSymbols = Array.from(splitSymbols).includes(value2[0]);
    if (regExp.test(value2) || !isFirstCharIsOneOfSplitSymbols) {
      setInputValue(value2);
    }
    if (onChange) {
      onChange(event);
    }
  };
  const handleDeleteChip = (chipComponentIndex = -1) => {
    chipArrayValues.splice(chipComponentIndex, 1);
    const newValues = [...chipArrayValues];
    setChipArrayValues(newValues);
    if (onRemove) {
      onRemove(newValues);
    }
  };
  const handleAddChip = () => {
    const newValues = [...chipArrayValues, inputValue];
    setChipArrayValues(newValues);
    setInputValue("");
    if (onAdd) {
      onAdd(newValues);
    }
  };
  const handleKeyDown = (event) => {
    if ((event.key === "Backspace" || event.key === "Delete") && inputValue === "") {
      handleDeleteChip();
    }
    const regExp = new RegExp(regExpForInputText);
    if (event.key === "Enter" && inputValue !== "" && regExp.test(inputValue)) {
      event.preventDefault();
      handleAddChip();
    }
    if (onKeyDown) {
      onKeyDown(event);
    }
  };
  const handleFocus = (event) => {
    if (onFocus) {
      onFocus(event);
    }
  };
  const handleBlur = (event) => {
    const regExp = new RegExp(regExpForInputText);
    if (addChipOnBlur && inputValue !== "" && regExp.test(inputValue)) {
      handleAddChip();
    }
    if (onBlur) {
      onBlur(event);
    }
  };
  return /* @__PURE__ */ React.createElement(Input, __spreadProps(__spreadValues({
    ref,
    inputRef,
    wrapperRef: wrapperRefProp,
    id,
    placeholder,
    type,
    size,
    shape,
    variant,
    isDisabled,
    isReadOnly,
    autoComplete,
    onChange: handleInputChange,
    onKeyDown: handleKeyDown,
    onFocus: handleFocus,
    onBlur: handleBlur,
    value: inputValue,
    isMultiline
  }, props), {
    addonLeft: [
      ...[addonLeft],
      !!(chipArrayValues == null ? void 0 : chipArrayValues.length) && (isMultiline ? /* @__PURE__ */ React.createElement(React.Fragment, null, chipArrayValues.map((value2, index) => /* @__PURE__ */ React.createElement(ChipComponent, {
        key: `${index}-${value2}`,
        size,
        onDismiss: () => handleDeleteChip(index),
        value: getItemsLabel(value2),
        isDisabled,
        isMultiline
      }))) : /* @__PURE__ */ React.createElement(ChipWrapper, null, chipArrayValues.map((value2, index) => /* @__PURE__ */ React.createElement(ChipComponent, {
        key: `${index}-${value2}`,
        size,
        onDismiss: () => handleDeleteChip(index),
        value: getItemsLabel(value2),
        isDisabled,
        sx: {
          marginLeft: "xxs",
          p: "0px 6px 0px 10px"
        }
      }))))
    ]
  }));
});
ChipInput.displayName = "ChipInput";

export { ChipInput };
//# sourceMappingURL=ChipInput.es.js.map
